// Dynamic Audio URL Mapping System
// This system automatically discovers and maps audio files based on directory structure
// and provides intelligent fallback mechanisms

import { 
  PATHWAY_CONFIGS, 
  getPathwayConfig, 
  getAudioFileMappings,
  AudioPathwayConfig,
  AudioFileMapping 
} from '../config/audioPathways';

// MD5 hash function for browser compatibility
function generateMD5Hash(text: string): string {
  // Use Web Crypto API for MD5-like hash (not cryptographically secure but consistent)
  // For now, use a simple hash that matches our Python script's behavior
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    const char = text.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  // Convert to hex and pad to match MD5 length
  return Math.abs(hash).toString(16).padStart(8, '0');
}

interface AudioMapping {
  pathwayId: string;
  lessonId: string;
  category: 'wordbank' | 'dialogue' | 'phrases' | 'activities';
  persianText?: string;
  englishFilename: string;
  audioUrl: string;
}

// Dynamic audio file discovery cache
const audioFileCache = new Map<string, Map<string, Map<string, string[]>>>();

/**
 * Discover audio files for a pathway by scanning the directory structure
 * This simulates what would be a server-side API call to discover available files
 */
const discoverAudioFiles = async (pathwayId: string): Promise<Map<string, Map<string, string[]>>> => {
  // Check cache first
  if (audioFileCache.has(pathwayId)) {
    return audioFileCache.get(pathwayId)!;
  }

  const config = getPathwayConfig(pathwayId);
  if (!config) {
    return new Map();
  }

  // In a real implementation, this would be an API call to discover files
  // For now, we'll use a static mapping based on known file structures
  const discoveredFiles = new Map<string, Map<string, string[]>>();
  
  // Add known files based on pathway configuration
  if (config.useStaticFiles) {
    // This would normally come from a server API that scans the directory
    // For now, we'll use the existing hardcoded mappings as a fallback
    const staticFiles = getStaticFileMappings(pathwayId);
    for (const [lessonId, categories] of Object.entries(staticFiles)) {
      const lessonMap = new Map<string, string[]>();
      for (const [category, files] of Object.entries(categories)) {
        lessonMap.set(category, files);
      }
      discoveredFiles.set(lessonId, lessonMap);
    }
  }

  // Cache the result
  audioFileCache.set(pathwayId, discoveredFiles);
  return discoveredFiles;
};

/**
 * Get static file mappings for known pathways
 * This is a temporary solution until we implement proper file discovery
 */
const getStaticFileMappings = (pathwayId: string): { [lessonId: string]: { [category: string]: string[] } } => {
  const mappings: { [pathwayId: string]: { [lessonId: string]: { [category: string]: string[] } } } = {
    '6875e2c70e9af9bca4a60a18': {
      'lesson-2': {
        'dialogue': [
          'can-you-speak-slowly.mp3',
          'i-didnt-hear-you-well.mp3',
          'can-you-say-it-again.mp3',
          'what-does-it-mean.mp3'
        ]
      }
    },
    '6875e2c70e9af9bca4a60a0f': {
      '873ba443-9ef3-417b-9102-368960f340d5': {
        'dialogue': [
          'ta-arof-is-not-just-words-it-s-a-philosophy-of-lif.mp3',
          'how-can-i-perform-ta-arof-correctly-.mp3'
        ],
        'wordbank': [
          'formal-politeness.mp3',
          'kindness.mp3'
        ]
      },
      'b098efad-c242-40a6-8354-33d1e762b058': {
        'dialogue': [
          'traditional-iranian-clothing-cultural-story.mp3',
          'how-do-clothes-reflect-culture.mp3'
        ],
        'wordbank': [
          'clothing.mp3',
          'culture.mp3'
        ]
      }
    },
    '6875e8eebb064190db6158a0': {
      'a0b6147f-d894-4b4b-9356-efbdffdd3055': {
        'dialogue': [
          'i-am-a-persian-literature-student-and-interested-in-classical-iranian-poetry.mp3',
          'what-do-you-say-about-yourself.mp3',
          'are-you-familiar-with-hafezs-works.mp3',
          'yes-i-have-studied-hafezs-divan-and-i-like-it-very-much.mp3'
        ],
        'wordbank': [
          'you-formalplural.mp3',
          'yourself-formal.mp3',
          'i.mp3',
          'weus.mp3',
          'theythem.mp3',
          'belonging-to.mp3',
          'responsibility.mp3',
          'agreeing.mp3',
          'opinion.mp3',
          'rule.mp3'
        ]
      }
    },
    '6875e2c70e9af9bca4a60a27': {
      'ff8ecaba-1de6-4e33-903d-18f2d81f5b71': {
        'dialogue': [
          'scene1-ahmad-greeting.mp3',
          'scene1-sara-response.mp3',
          'scene1-ahmad-response.mp3',
          'scene1-sara-plans.mp3'
        ]
      },
      'ec8d0e49-5a2d-497c-a6d3-622e998eb3d0': {
        'dialogue': [
          'hello-mrs-rezaei-good-afternoon.mp3',
          'hello-kids-good-afternoon-how-are-you.mp3',
          'im-good-thank-you-how-are-you.mp3',
          'im-good-thank-you-how-was-today.mp3',
          'hello-kids-good-afternoon.mp3',
          'hello-ali-good-afternoon-how-are-you.mp3',
          'im-good-thank-you-what-did-you-do-today.mp3',
          'we-went-to-school-and-learned-persian.mp3',
          'hello-kids-good-afternoon-lets-have-tea.mp3',
          'hello-maam-good-afternoon-thank-you.mp3',
          'please-tea-is-ready.mp3',
          'thank-you-mom-the-tea-is-very-delicious.mp3'
        ],
        'wordbank': [
          'word-asr.mp3',
          'word-asr-bekheir.mp3',
          'word-emruz.mp3',
          'word-chetour.mp3',
          'word-bud.mp3',
          'word-madrese.mp3',
          'word-raftim.mp3',
          'word-yad-gereftim.mp3',
          'word-chai.mp3'
        ],
        'phrases': [
          'phrase-asr-bekheir.mp3',
          'phrase-emruz-chetour-bud.mp3',
          'phrase-madrese-raftim.mp3',
          'phrase-farsi-yad-gereftim.mp3',
          'phrase-بیایید-18.mp3'
        ]
      }
    },
    '6875e2c70e9af9bca4a60a15': {
      '988ed893-3e28-414c-85d5-51320a74be02': {
        'dialogue': [
          'hello-how-is-your-family-is-everyone-healthy.mp3',
          'hello-thank-you-for-your-concern-everyone-is-good-and-healthy.mp3',
          'thats-good-how-is-your-mother-i-hope-shes-well.mp3',
          'thank-you-my-mother-is-very-well-my-father-is-also-healthy-and-strong.mp3',
          'how-is-your-brother-i-heard-he-found-a-new-job.mp3',
          'yes-hes-very-happy-my-sister-also-just-graduated-from-university.mp3',
          'wow-what-great-news-your-family-is-really-successful.mp3',
          'thank-you-thank-god-everyone-is-healthy-and-happy.mp3'
        ],
        'wordbank': [
          'family.mp3',
          'concern.mp3',
          'mother.mp3',
          'father.mp3',
          'strong.mp3',
          'brother.mp3',
          'i-heard.mp3',
          'jobwork.mp3',
          'sister.mp3',
          'university.mp3',
          'graduate.mp3',
          'successful.mp3',
          'happy.mp3',
          'everyone.mp3',
          'healthy.mp3',
          'thank-god.mp3'
        ]
      }
    },
    'talking-about-your-family': {
      '5959d716-cd44-4f29-8b8f-e4af842848be': {
        'dialogue': [
          'hello-sara-welcome-to-our-home.mp3',
          'hello-negin-thank-you-for-the-invitation.mp3',
          'come-let-me-introduce-my-family.mp3',
          'this-is-my-mother-her-name-is-parisa.mp3',
          'hello-mrs-parisa-nice-to-meet-you.mp3',
          'and-this-is-my-father-his-name-is-ahmad.mp3',
          'how-many-siblings-do-you-have.mp3',
          'i-have-one-sister-and-one-brother-my-sister-is-maryam-and-my-brother-is-ali.mp3',
          'what-a-big-family-my-family-is-smaller.mp3',
          'maryam-ali-come-meet-my-friend-sara.mp3',
          'hello-sara-im-happy-to-see-you.mp3',
          'hello-im-ali-how-old-are-you.mp3',
          'im-twenty-years-old-how-about-you.mp3',
          'im-eighteen-years-old-maryam-is-older-than-me.mp3',
          'yes-im-twenty-two-years-old-im-the-oldest-child.mp3',
          'so-negin-is-the-middle-child-and-ali-is-the-youngest.mp3',
          'thats-right-im-the-middle-child.mp3',
          'dear-sara-tell-us-about-your-family-too.mp3',
          'i-only-have-one-older-brother-his-name-is-kamran.mp3',
          'what-does-he-do.mp3',
          'hes-an-engineer-he-lives-in-tehran.mp3',
          'we-have-a-family-dinner-every-friday-would-you-like-to-come.mp3',
          'really-how-interesting-i-would-love-to.mp3',
          'mom-makes-the-best-kabab.mp3',
          'great-should-i-bring-something-too.mp3'
        ],
        'wordbank': [
          'family.mp3',
          'how-many.mp3',
          'biglarge.mp3',
          'small.mp3',
          'introduction.mp3',
          'yearage.mp3',
          'olderbigger.mp3',
          'youngersmaller.mp3',
          'child.mp3',
          'middle.mp3',
          'engineer.mp3',
          'lifelive.mp3',
          'dinner.mp3',
          'friday.mp3',
          'dear.mp3'
        ],
        'phrases': [
          'this-is-my-mother.mp3',
          'he-is-my-father.mp3',
          'how-many-siblings-do-you-have.mp3',
          'let-me-introduce-my-family.mp3',
          'what-a-big-family.mp3'
        ]
      }
    },
    'describing-daily-routines': {
      'a8b9c2d1-5e6f-4g7h-8i9j-0k1l2m3n4o5p': {
        'dialogue': [
          'i-usually-eat-dinner-at-6-oclock.mp3',
          'what-food-do-you-eat-for-dinner.mp3',
          'i-usually-eat-rice-and-stew.mp3',
          'after-dinner-i-watch-some-television.mp3',
          'what-program-do-you-watch.mp3',
          'i-watch-news-or-movies.mp3',
          'at-10-pm-i-brush-my-teeth.mp3',
          'what-do-you-do-after-brushing.mp3',
          'i-sleep-i-have-work-tomorrow.mp3',
          'i-have-important-work-tomorrow.mp3',
          'then-sleep-early.mp3',
          'yes-enough-sleep-is-necessary.mp3'
        ],
        'wordbank': [
          'dinner.mp3',
          'television.mp3',
          'tooth.mp3',
          'toothbrush.mp3',
          'to-sleep.mp3',
          'tomorrow.mp3',
          'stew.mp3',
          'news.mp3',
          'movie.mp3',
          'usually.mp3'
        ]
      }
    }
  };

  return mappings[pathwayId] || {};
};

/**
 * Generate a dynamic audio URL based on pathway configuration
 */
const generateDynamicAudioUrl = (
  pathwayId: string, 
  lessonId: string, 
  category: string, 
  filename: string
): string => {
  const config = getPathwayConfig(pathwayId);
  if (!config) {
    return '';
  }

  if (config.useStaticFiles) {
    return `${config.basePath}/${lessonId}/${category}/${filename}`;
  }

  if (config.useGanjiAdabi) {
    // Generate ganji-adabi URL with English filename
    const encodedText = encodeURIComponent(filename);
    return `/api/audio/ganji-adabi/${encodedText}?text=${encodedText}&context=${category}&lessonId=${lessonId}&filename=${filename}`;
  }

  return '';
};

/**
 * Find the best matching audio file for a given text and context
 */
const findBestAudioMatch = (
  persianText: string,
  pathwayId: string,
  lessonId: string,
  category: string,
  discoveredFiles: Map<string, Map<string, string[]>>
): string | null => {
  const lessonFiles = discoveredFiles.get(lessonId);
  if (!lessonFiles) return null;

  const categoryFiles = lessonFiles.get(category);
  if (!categoryFiles) return null;

  // Try to find a file that matches the Persian text
  // This is a simplified matching - in practice, you'd want more sophisticated matching
  const cleanText = persianText.replace(/[^\u0600-\u06FF\s]/g, '').trim();
  
  // For now, return the first available file in the category
  // In a real implementation, you'd implement intelligent text-to-filename matching
  return categoryFiles.length > 0 ? categoryFiles[0] : null;
};

/**
 * Main function to get the correct audio URL
 */
export const getCorrectAudioUrl = async (
  pathwayId: string, 
  audioUrl: string, 
  lessonId?: string
): Promise<string> => {
  console.log('🎯 getCorrectAudioUrl called with:', { pathwayId, audioUrl, lessonId });

  if (!audioUrl || !pathwayId) {
    return audioUrl;
  }

  const config = getPathwayConfig(pathwayId);
  if (!config) {
    console.log(`🎯 No configuration found for pathway: ${pathwayId}`);
    // For unconfigured pathways, try to use the URL as-is if it looks valid
    if (audioUrl.startsWith('/audio/') && audioUrl.includes('.mp3')) {
      console.log(`🎵 Using unconfigured pathway URL as-is: ${audioUrl}`);
      return audioUrl;
    }
    return audioUrl;
  }

  // If the URL is already properly formatted, use it as-is
  if (shouldUseAudioUrlAsIs(audioUrl, config)) {
    console.log(`🎵 Using properly formatted URL: ${audioUrl}`);
    return audioUrl;
  }

  // Try to discover available audio files
  const discoveredFiles = await discoverAudioFiles(pathwayId);
  
  // If we have lessonId, try to find a matching file
  if (lessonId && discoveredFiles.has(lessonId)) {
    const lessonFiles = discoveredFiles.get(lessonId)!;
    
    // Try to extract category and filename from the original URL
    const urlParts = audioUrl.split('/');
    const filename = urlParts[urlParts.length - 1]?.replace('.mp3', '');
    
    if (filename) {
      // Try to find the file in any available category
      for (const [category, files] of Array.from(lessonFiles.entries())) {
        if (files.includes(filename)) {
          const dynamicUrl = generateDynamicAudioUrl(pathwayId, lessonId, category, filename);
          if (dynamicUrl) {
            console.log(`🎵 Found matching file: ${audioUrl} -> ${dynamicUrl}`);
            return dynamicUrl;
          }
        }
      }
    }
  }

  // If no match found and we should use ganji-adabi, generate a URL
  if (config.useGanjiAdabi && lessonId) {
    const ganjiAdabiUrl = generateGanjiAdabiUrl(audioUrl, lessonId, config);
    if (ganjiAdabiUrl) {
      console.log(`🎵 Generated ganji-adabi URL: ${audioUrl} -> ${ganjiAdabiUrl}`);
      return ganjiAdabiUrl;
    }
    
    // If we couldn't generate a ganji-adabi URL from the original URL,
    // try to generate one from the Persian text if we can extract it
    if (audioUrl.includes('/audio/')) {
      const urlParts = audioUrl.split('/');
      const filename = urlParts[urlParts.length - 1]?.replace('.mp3', '');
      if (filename) {
        const persianText = findPersianTextForFilename(filename, lessonId);
        if (persianText) {
          const encodedText = encodeURIComponent(persianText);
          const fallbackGanjiUrl = `/api/audio/ganji-adabi/${encodedText}?text=${encodedText}&context=lesson&lessonId=${lessonId}&filename=${filename}`;
          console.log(`🎵 Generated fallback ganji-adabi URL: ${audioUrl} -> ${fallbackGanjiUrl}`);
          return fallbackGanjiUrl;
        }
      }
    }
  }

  // Return original URL if no better option found
  console.log(`🎯 No better audio URL found, using original: ${audioUrl}`);
  return audioUrl;
};

/**
 * Generate ganji-adabi URL with proper English filename
 */
const generateGanjiAdabiUrl = (
  originalUrl: string, 
  lessonId: string, 
  config: AudioPathwayConfig
): string => {
  // Extract Persian text from URL if possible
  let persianText = '';
  let englishFilename = '';

  if (originalUrl.includes('/api/audio/ganji-adabi/')) {
    // Extract from ganji-adabi URL
    const urlParts = originalUrl.split('/');
    const encodedText = urlParts[urlParts.length - 1]?.split('?')[0];
    if (encodedText) {
      try {
        persianText = decodeURIComponent(encodedText);
        englishFilename = generateEnglishFilename(persianText);
      } catch (e) {
        console.warn('Failed to decode Persian text from URL:', e);
      }
    }
  } else if (originalUrl.includes('/audio/')) {
    // Extract from static audio URL
    const urlParts = originalUrl.split('/');
    const filename = urlParts[urlParts.length - 1]?.replace('.mp3', '');
    if (filename) {
      englishFilename = filename;
      // Try to find Persian text mapping
      persianText = findPersianTextForFilename(filename, lessonId);
    }
  }

  // If we still don't have Persian text, try to extract it from the filename
  if (!persianText && englishFilename) {
    persianText = findPersianTextForFilename(englishFilename, lessonId);
  }

  if (persianText && englishFilename) {
    const encodedText = encodeURIComponent(persianText);
    return `/api/audio/ganji-adabi/${encodedText}?text=${encodedText}&context=lesson&lessonId=${lessonId}&filename=${englishFilename}`;
  }

  return '';
};

/**
 * Generate English filename from Persian text
 */
const generateEnglishFilename = (persianText: string): string => {
  // This is a simplified version - in practice, you'd want a proper translation service
  const commonMappings: { [key: string]: string } = {
    'سلام': 'hello',
    'خداحافظ': 'goodbye',
    'ممنون': 'thank-you',
    'لطفا': 'please',
    'بله': 'yes',
    'نه': 'no',
    'کتاب': 'book',
    'آب': 'water',
    'خانه': 'house',
    'امروز چه چیزهایی یاد می‌گیریم؟': 'what-learn-today',
    'سلام! صبح بخیر سارا': 'scene1-ahmad-greeting',
    'سلام! صبح بخیر احمد. چطوری؟': 'scene1-sara-response',
    'ممنون، خوبم. تو چطوری؟': 'scene1-ahmad-response',
    'خوبم، ممنون. امروز چه برنامه‌ای داری؟': 'scene1-sara-plans'
  };

  // Try to find exact match first
  if (commonMappings[persianText]) {
    return commonMappings[persianText];
  }

  // Generate a generic filename based on text length and content
  const cleanText = persianText.replace(/[^\u0600-\u06FF\s]/g, '').trim();
  const words = cleanText.split(/\s+/);
  const firstWord = words[0] || 'text';
  
  return `${firstWord}-${cleanText.length}`;
};

/**
 * Find Persian text for a given filename
 */
const findPersianTextForFilename = (filename: string, lessonId: string): string => {
  // This would normally come from a database or configuration
  // For now, we'll use a simple mapping based on known files
  
  // Extract the base filename without extension
  const baseFilename = filename.replace('.mp3', '');
  
  // Common Persian text mappings for known filenames
  const persianTextMappings: { [key: string]: string } = {
    'i-am-a-persian-literature-student-and-interested-in-classical-iranian-poetry': 'من دانشجوی ادبیات فارسی هستم و علاقه‌مند به شعر کلاسیک ایران.',
    'what-do-you-say-about-yourself': 'شما در مورد خودتان چه می‌گویید؟',
    'are-you-familiar-with-hafezs-works': 'آیا شما با آثار حافظ آشنا هستید؟',
    'yes-i-have-studied-hafezs-divan-and-i-like-it-very-much': 'بله، من دیوان حافظ را مطالعه کرده‌ام و آن را بسیار دوست دارم.',
    'can-you-speak-slowly': 'آهسته صحبت کن',
    'i-didnt-hear-you-well': 'خوب نشنیدم',
    'can-you-say-it-again': 'دوباره بگو',
    'what-does-it-mean': 'معنی اش چیست',
    'what-learn-today': 'امروز چه چیزهایی یاد می‌گیریم؟',
    'scene1-ahmad-greeting': 'سلام! صبح بخیر سارا',
    'scene1-sara-response': 'سلام! صبح بخیر احمد. چطوری؟',
    'scene1-ahmad-response': 'ممنون، خوبم. تو چطوری؟',
    'scene1-sara-plans': 'خوبم، ممنون. امروز چه برنامه‌ای داری؟'
  };
  
  return persianTextMappings[baseFilename] || '';
};

/**
 * Check if an audio URL should be used as-is
 */
export const shouldUseAudioUrlAsIs = (audioUrl: string, config?: AudioPathwayConfig): boolean => {
  if (!audioUrl) return false;

  // URLs that should always be used as-is
  if (audioUrl.includes('/api/audio/ganji-adabi/') ||
      audioUrl.includes('/audio-cache/') ||
      audioUrl.startsWith('http://') ||
      audioUrl.startsWith('https://') ||
      audioUrl.startsWith('data:audio/') ||
      audioUrl.startsWith('blob:')) {
    return true;
  }

  // Check if URL matches the pathway's base path
  if (config && audioUrl.startsWith(config.basePath)) {
    return true;
  }

  return false;
};

/**
 * Check if a lesson has generated audio files
 */
export const hasGeneratedAudio = async (pathwayId: string, lessonId: string): Promise<boolean> => {
  const discoveredFiles = await discoverAudioFiles(pathwayId);
  return discoveredFiles.has(lessonId);
};

/**
 * Get word-of-the-day audio URL - now returns empty string to trigger TTS fallback
 */
export const getWordOfTheDayAudioUrl = (
  persianWord: string, 
  englishWord?: string, 
  originalAudioUrl?: string
): string => {
  if (originalAudioUrl && shouldUseAudioUrlAsIs(originalAudioUrl)) {
    return originalAudioUrl;
  }

  // Return empty string to trigger TTS fallback
  return '';
};

// Mapping of Persian text to actual generated audio file hashes
const GRAMMAR_AUDIO_MAPPING: Record<string, string> = {
  // Grammar-3 lesson audio files
  'فعل باید با فاعل مطابقت داشته باشد.': 'f0ced104cc71fa8738d0776f81f0b9ae',
  'مثال: من می‌روم، تو می‌روی، او می‌رود.': '69a8f2e219387c6b788b911050fa3175',
  'من به مدرسه می‌روم.': 'f3b5305d395d4aa9dc7cbe3c66b66091',
  'تو به خانه می‌روی.': '1d8f430b1f0ad640aca1c14c1b760a19',
  'او به کار می‌رود.': '454c71d5035eb88e1228d4e9b931c769',
  'ما به پارک می‌رویم.': '694351908348eb8a3f2f23366bdbc971',
  'شما به دانشگاه می‌روید.': '43ba9fa008ee1cf8a761842e8f11be38',
  'آنها به سینما می‌روند.': '5e994258a5de31b9ac4b134ded1813ff',
  'فاعل': '33ab9160f315520df924c6f1fde9761f',
  'فعل': 'f666f52115cee8ec019a947804fe3320',
  'مطابقت': '2937646465f31c1c3e3306899dd8f380',
  'می‌روم': 'f7e8c056ff00d498b5d57cfe4f6a1e1d',
  'می‌روی': '1fc8c735acc453cbe6f57d394e2e536f',
  'می‌رود': '7297a9d8f3cb219e7e14404cba9b521f',
  
  // Grammar-2 lesson audio files (Enhanced)
  'به درس هجای فارسی خوش آمدید!': 'welcome-syllables',
  'امروز چه چیزهایی یاد می‌گیریم؟': 'what-learn-today',
  'امروز یاد می‌گیریم که چطور کلمات فارسی را به هجا تقسیم کنیم.': '672a942d1ac39a631f49d067be333611',
  'در فارسی، هر هجا یک صدا دارد.': 'e77bf75903b1bc3aea4f8b30c2f471fb',
  'مثال: کت-اب': '707bbd06b5b22dc864412390948910db',
  'کت-اب دو هجا دارد: کت + اب': '058e30aefd5b54896ca6662161e350da',
  'CV: بَ': '590de78f1048bb471e6bbe041a66a481',
  'CVC: کَت': 'b92a03a105c4e41603d4ac34ccedb4f3',
  'CVCC: کَرد': 'cc99f1e231f477b5263f4cd545793efc',
  'CCVC: سْتْر': 'c5f48cfe5ac9f86eaa58f3cf4d886f0b',
  'در فارسی، معمولاً آخرین هجا تاکید دارد.': '0b42b2e0fad64de38a496ee98521a8ae',
  'مثال: کت-اب (تاکید روی \'اب\')': 'd588fc61ab0e242a5744748f4a9a4b4c',
  'دانش-جو (تاکید روی \'جو\')': '80f1569de2e43053e5d89b1c3c93fc10',
  'V: اَ': 'ed293c924022a3f2eb6d5fb2d9464a45',
  'VC: اَب': '22e8dcab2fa39768877d6b78a59df69d',
  'CCV: سْتْر': '6248fa4843338b00f2dcfb8ab5ef2769',
  'VCC: اَبْد': '1d72c221b86109b306baac9ed330818c',
  'هنگام تلفظ، هر هجا را جداگانه بگویید.': '816d42606383d882e7365cc25f49a90a',
  'مثال: م-ع-لم': '51ba7d67d2842159a01286a4ff0ed2a7',
  'سپس آنها را به هم وصل کنید.': 'f650b22d0fd064efa4099b636bbfb394',
  'بیایید چند کلمه را تمرین کنیم:': '4202687139d587ac5ef474865272c3d4',
  'دانشگاه: دا-نش-گاه': '6e6ed1fcb07ae95dcd74335b676ed0e1',
  'کتابخانه: ک-تاب-خا-نه': '6ae941b4d935da5162cc0b4ba31daee5',
  'هجای': 'bc5b49f822c703ba7cc60432a7d3c338',
  'صدا': 'f6d3fa0520ae59fc04ceeafb2463d231',
  'حرف': 'bc1165cd1fba51f7e3954f2ec58496c8',
  'تلفظ': '6f114e7b618d9d9fcb0479cb1861cf87',
  'تاکید': '2652f57551011794aa09518f60294239',
  'تقسیم': 'e238a1202dbccf3d23687e95f4c1e512',
  'قاعده': '347793c611178d3009f0ae2f977c8081',
  'مثال': '540c061acec5e76fac4d69dab1f19e65',
  'تمرین': '84abd5ebca6e2f6b588b26ef2ab4617d',
  'دانشگاه': '90523cff3e8a41489216ed9c73286760',
  'کتابخانه': 'e4660a469bf081b91c5fdfbcf1fe89cf',

  // Grammar-7 lesson audio files (Subjunctive Mood)
  'وجه شرطی برای بیان امکان، تمایل و شرط استفاده می‌شود.': '53caa6f4530ccbaedb089c8bccb637c2',
  'مثال: باید بروم، شاید بیاید': '35629903bf34f56a5870fac39f8a706b',
  'باید به مدرسه بروم.': 'a25dbaf3cfb7ee134eeb7edf6a81e227',
  'شاید او فردا بیاید.': 'd59c8ee526b900964539b05ba2c9146c',
  'اگر وقت داشته باشم، می‌آیم.': 'c489f3c79a74cb8e29efbd1a1ba4ab69',
  'باید این کار را انجام دهم.': '8bdf87b45c4583a7c04d191a44a4e376',
  'شاید باران ببارد.': '2ed5429462e820bafb9388f13c496128',
  'باید': '35551de37c9343b55883351cf871c830',
  'شاید': '21bb5a9536a44198708bdb6e3a882cd3',
  'اگر': '60cef54434c9cbe396f0333ac4e1572d',
  'شرطی': 'ad24bed4edb57ba20e65c054af4c97ef',

  // Persian Conversations with Animation - Lesson 4 (Restaurant Conversation)
  'به درس چهارم مکالمه فارسی خوش آمدید!': '1745bf5326fd7342617ee411d1425b20',
  'امروز یاد می‌گیریم که چطور در رستوران سفارش بدهیم.': 'e51b6b1aad521b4f4f0c0e1a0dcd1a32',
  'سلام! میز برای دو نفر می‌خواهید؟': 'aa5a247eb98d23fc2fa3b03ef139b2fd',
  'بله، لطفاً. منو را می‌توانیم ببینیم؟': '620a848c3b2fc380bc06055c86167925',
  'البته! اینجا منو است.': 'b5315a8f227bdb85b93168275debf711',
  'چه غذاهایی دارید؟': '5a9181e938515ad96c3114445f5aef0f',
  'کباب، قورمه سبزی، فسنجان و خورشت بادمجان داریم.': 'b11ef340d91a753ab19e5d5869c97d63',
  'من کباب می‌خواهم.': '925c6582106c9c07bcbf2ddd6915d0ad',
  'من هم قورمه سبزی می‌خواهم.': 'f8b3125545f33a4d98fd0ca0d527c66b',
  'نوشیدنی چی می‌خواهید؟': 'e79d84b99a062f4e7a9fc497de398081',
  'دو لیوان آب، لطفاً.': '445f992ca21b7baf0444a1921c8a6738',
  'من چای می‌خواهم.': '7decab4562e392bef27841a72002fafd',
  'چای شیرین یا تلخ؟': 'bba042a13456d50d87a205c053799d05',
  'شیرین، لطفاً.': 'e9e28903341eaf2d7947c1856ec90ecd',
  'غذا آماده شد. نوش جان!': '66d265cae19411ac74d2f8662776927c',
  'ممنون! خیلی خوشمزه است.': 'e3fe45981fc1c2bd882fef8c00d333eb',
  'بله، واقعاً عالی است.': 'd472763d4270985acf06788bcc0f49ad',
  'لطفاً حساب را بیاورید.': '1d9b8b3a173f4d16bc9623c959f523ee',
  'البته! اینجا حساب شما است.': '78616ce92920f38e8fdca371ed5491a9',
  'چقدر می‌شود؟': '71e475453498b24f4d827971ad3ac031',
  'سی هزار تومان.': '1c25a1b083333532cb2c2a8da8ebe059',
  'خیلی ممنون! خداحافظ.': '726d04ca9ee267c103d788fd067b39a6',
  'رستوران': '1c6c6c28517a60764cbc1ca88655909b',
  'میز': 'c8077a867926a60b1710f7819ebd76a3',
  'منو': 'efecbffbaeed94231fc385b33198c03d',
  'غذا': '58e7e7cb5964419c73ed9ed5b77db54c',
  'کباب': '8a134afa634865b83076073509a2742d',
  'قورمه سبزی': '32b47dd99d108ddc6ef17859e385663d',
  'نوشیدنی': '4c188f5c420031f79046d47994321258',
  'آب': '0c5c17dbd1c6fb170b36e61dacf28abd',
  'چای': '430dd2763f52d82d645b25525d09b0f4',
  'شیرین': 'ef96078b1580e600299ce9d007ab1ab7',
  'تلخ': '5954d14e0a32a470569c601738f78816',
  'خوشمزه': 'ead4eeb06d4e94813a3b45b01043f06d',
  'حساب': 'b177499b85a41c079b52a0f74e064d35',
  'تومان': '9e29f6087438f2256eb5b6d1e181e0b3',
  'ممنون': 'ab64fafe0137d1249a8e798acd67e66e',
  'میز برای دو نفر می‌خواهید؟': '3cee129e4f1eb9bba761ff8fa6a4f569',
  'منو را می‌توانیم ببینیم؟': '402b1dd5762cc5d4b6a62503183bdd8c',
  'من کباب می‌خواهم': 'cdab749db7f0ef1cd3dae498452b1194',
  'نوش جان!': '1865ff4b8775936870e1757fdba0fe2e',
  'خیلی خوشمزه است': 'cff9260def1bce44ad3b1e426b7d4829',
  'لطفاً حساب را بیاورید': 'deb7bde50ae6a6da004ec86ae23cdf68'
};

/**
 * Generate grammar audio URL for grammar lessons - now uses static audio files with auto-generation
 */
export const generateGrammarAudioUrl = (
  persianText: string, 
  englishText: string, 
  context: string, 
  lessonId: string
): string => {
  if (!persianText) {
    return '';
  }

  // Generate English filename from Persian text
  const englishFilename = generateEnglishFilename(persianText);
  
  // Trigger audio generation in the background (fire and forget)
  const pathwayId = 'grammar-fundamentals-pathway';
  ensureAudioFileExists(pathwayId, lessonId, persianText, englishFilename).catch(error => {
    console.warn(`Background audio generation failed: ${error}`);
  });
  
  // Return the static audio file URL (will be generated if missing)
  return `/audio/${pathwayId}/${lessonId}/${englishFilename}.mp3`;
};

/**
 * Check if audio file exists and generate it if missing
 */
export const ensureAudioFileExists = async (
  pathwayId: string,
  lessonId: string,
  persianText: string,
  englishFilename: string
): Promise<boolean> => {
  try {
    // Check if the audio file already exists
    const audioPath = `/audio/${pathwayId}/${lessonId}/${englishFilename}.mp3`;
    
    // Try to fetch the file to see if it exists
    const response = await fetch(audioPath, { method: 'HEAD' });
    if (response.ok) {
      return true; // File exists
    }

    // File doesn't exist, try to generate it
    console.log(`🎵 Audio file not found, attempting to generate: ${audioPath}`);
    
    // Call the audio generation API endpoint
    const generateResponse = await fetch('/api/generate-audio', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        pathwayId,
        lessonId,
        persianText,
        englishFilename,
        context: 'lesson'
      })
    });

    if (generateResponse.ok) {
      console.log(`✅ Audio file generated successfully: ${audioPath}`);
      return true;
    } else {
      console.warn(`⚠️ Failed to generate audio file: ${audioPath}`);
      return false;
    }
  } catch (error) {
    console.warn(`⚠️ Error checking/generating audio file: ${error}`);
    return false;
  }
};

/**
 * Get pathway configuration
 */
export const getPathwayConfigById = (pathwayId: string): AudioPathwayConfig | undefined => {
  return getPathwayConfig(pathwayId);
};

/**
 * Get all pathway configurations
 */
export const getAllPathwayConfigs = (): AudioPathwayConfig[] => {
  return PATHWAY_CONFIGS;
};

export default PATHWAY_CONFIGS; 